/**
 * Single source of truth for status → chip color across the Promo workspace (plan-011 §8 intro).
 * Chips always carry their text label (English enum, via i18n) — color is never the only signal.
 */
import type { Theme } from '@mui/material';

import type { CampaignStatus, CouponCodeState, CouponStatus, PromotionStatus } from './types';

export type StatusChipColor = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';

/**
 * Resolve a {@link StatusChipColor} to a solid hex for non-Chip surfaces (calendar bars, burn
 * bars). `theme.palette['grey']` has no `.main` (it's a shade map, not a `PaletteColor`) so this
 * branches explicitly instead of bracket-indexing the palette with a color-name union — that
 * indexing pattern doesn't type-check once 'default' is in the union.
 */
export function statusColorHex(theme: Theme, color: StatusChipColor): string {
  switch (color) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    case 'primary':
      return theme.palette.primary.main;
    default:
      return theme.palette.grey[500];
  }
}

export const PROMOTION_STATUS_COLOR: Record<PromotionStatus, StatusChipColor> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  SCHEDULED: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'default',
  ARCHIVED: 'default',
  REJECTED: 'error',
};

export const CAMPAIGN_STATUS_COLOR: Record<CampaignStatus, StatusChipColor> = {
  DRAFT: 'default',
  SCHEDULED: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'default',
  ARCHIVED: 'default',
};

export const COUPON_STATUS_COLOR: Record<CouponStatus, StatusChipColor> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'default',
};

export const COUPON_CODE_STATE_COLOR: Record<CouponCodeState, StatusChipColor> = {
  ACTIVE: 'success',
  DEACTIVATED: 'default',
  EXHAUSTED: 'warning',
};

/** Statuses from which a promotion may still be edited (PUT /promotions/{id} is legal). */
export const PROMOTION_EDITABLE_STATUSES: PromotionStatus[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'REJECTED',
  'SCHEDULED',
];

/** Non-terminal promotion statuses (archive is legal from any of these, per the BFF contract). */
export const PROMOTION_NON_TERMINAL_STATUSES: PromotionStatus[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SCHEDULED',
  'ACTIVE',
  'PAUSED',
  'REJECTED',
];
